import { useCallback, useMemo } from 'react';

import type { EnhancedItem } from '../../hooks/useEnhancedItems';
import { getSelectedItems } from '../../utils/itemLookup';

type UseItemSelectionProps<Item> = {
  value: string[];
  items: Item[];
  enhancedFilteredItems: EnhancedItem<Item>[];
  itemToStringValue: (item: Item) => string;
  onChange?: (params: { values: string[]; selectedItems: Item[] }) => void;
};

type UseItemSelectionReturn<Item> = {
  handleItemSelection: (selectedItem: EnhancedItem<Item>) => void;
  removeLastItemFromSelection: () => void;
  selectedItems: Item[];
  addToSelection: (items: Item | Item[]) => void;
  removeFromSelection: (items: Item | Item[]) => void;
};

export const useItemSelection = <Item>({
  value,
  items,
  enhancedFilteredItems,
  itemToStringValue,
  onChange,
}: UseItemSelectionProps<Item>): UseItemSelectionReturn<Item> => {
  const selectedItems = useMemo(
    (): Item[] => getSelectedItems(value, items, itemToStringValue),
    [value, items, itemToStringValue]
  );

  const addToSelection = useCallback(
    (items: Item | Item[]): void => {
      const itemsArray = Array.isArray(items) ? items : [items];
      const newValues = itemsArray
        .map((item) => itemToStringValue(item))
        .filter((val) => !value.includes(val)); // dont want to add items that are already selected
      const newItems = itemsArray.filter((item) =>
        newValues.includes(itemToStringValue(item))
      );

      onChange?.({
        values: [...value, ...newValues],
        selectedItems: [...selectedItems, ...newItems],
      });
    },
    [onChange, itemToStringValue, value, selectedItems]
  );

  const removeFromSelection = useCallback(
    (items: Item | Item[]): void => {
      const itemsArray = Array.isArray(items) ? items : [items];
      const valuesToRemove = itemsArray
        .map((item) => itemToStringValue(item))
        .filter((val) => value.includes(val)); // only remove items that are currently selected

      const remainingValues = value.filter(
        (val) => !valuesToRemove.includes(val)
      );
      const remainingItems = selectedItems.filter(
        (item) => !valuesToRemove.includes(itemToStringValue(item))
      );

      onChange?.({
        values: remainingValues,
        selectedItems: remainingItems,
      });
    },
    [onChange, itemToStringValue, value, selectedItems]
  );

  const getEnabledItems = useCallback((items: EnhancedItem<Item>[]): Item[] => {
    return items
      .filter(
        (item): item is Extract<EnhancedItem<Item>, { itemType: 'regular' }> =>
          !item.isDisabled && item.itemType === 'regular'
      )
      .map((item) => item.item);
  }, []);

  const getGroupItems = useCallback(
    (groupLabel: string): EnhancedItem<Item>[] => {
      const groupItems = enhancedFilteredItems.filter(
        (item) => item.groupLabel === groupLabel && item.itemType === 'regular'
      );
      return groupItems;
    },
    [enhancedFilteredItems]
  );

  const handleSelectionToggle = useCallback(
    (targetItems: Item[], isCurrentlySelected: boolean): void => {
      if (isCurrentlySelected) {
        removeFromSelection(targetItems);
      } else {
        addToSelection(targetItems);
      }
    },
    [addToSelection, removeFromSelection]
  );

  const handleItemSelection = useCallback(
    (selectedItem: EnhancedItem<Item>): void => {
      switch (selectedItem.itemType) {
        case 'selectAll': {
          const allEnabledItems = getEnabledItems(enhancedFilteredItems);
          handleSelectionToggle(allEnabledItems, selectedItem.isSelected);
          break;
        }

        case 'groupLabel': {
          if (selectedItem.groupLabel) {
            const groupItems = getGroupItems(selectedItem.groupLabel);
            const allEnabledItems = getEnabledItems(groupItems);
            handleSelectionToggle(allEnabledItems, selectedItem.isSelected);
          }
          break;
        }

        case 'regular': {
          handleSelectionToggle([selectedItem.item], selectedItem.isSelected);
          break;
        }

        default: {
          // Exhaustive check to ensure all cases are handled at compile time
          const _exhaustiveCheck: never = selectedItem;
          return _exhaustiveCheck;
        }
      }
    },
    [
      enhancedFilteredItems,
      getEnabledItems,
      getGroupItems,
      handleSelectionToggle,
    ]
  );

  const removeLastItemFromSelection = useCallback((): void => {
    if (selectedItems.length === 0) {
      return;
    }
    const lastItem = selectedItems[selectedItems.length - 1];
    removeFromSelection(lastItem);
  }, [selectedItems, removeFromSelection]);

  return {
    handleItemSelection,
    removeLastItemFromSelection,
    selectedItems,
    removeFromSelection,
    addToSelection,
  };
};
